import { parseTimeStampOrNull } from '@/lib/parseTimeStamp';
import type { ChatListResponse, SessionListItem } from '@heidi/session/contracts';

/** The per-row shape `/v2/chat/chats` returns — mirrors `RecentChatRow`'s own
 *  minimal prop type rather than pulling the generated `ChatListItem` schema
 *  type in directly. */
export type RecentChatSource = ChatListResponse['chats'][number];

export interface RecentVisitItem {
  kind: 'visit';
  key: string;
  createdAt: number;
  visit: SessionListItem;
}

export interface RecentChatItem {
  kind: 'chat';
  key: string;
  createdAt: number;
  chat: RecentChatSource;
}

export type RecentItem = RecentVisitItem | RecentChatItem;

const toTimestamp = (value: string | null | undefined): number =>
  parseTimeStampOrNull(value, true)?.getTime() ?? 0;

/**
 * Combines loaded visits and chats into one reverse-chronological list keyed
 * on each source's own `created_at`. Visits and chats are always distinct
 * items — a chat that references a session is never merged into that
 * session's visit row.
 *
 * Equal timestamps break deterministically (visit before chat, then by key)
 * so the merged order never depends on the two sources' fetch/arrival order.
 */
export function mergeRecentItems(
  visits: SessionListItem[],
  chats: RecentChatSource[]
): RecentItem[] {
  const visitItems: RecentVisitItem[] = visits.map((visit) => ({
    kind: 'visit',
    key: `visit:${visit.session_id}`,
    createdAt: toTimestamp(visit.created_at),
    visit,
  }));
  const chatItems: RecentChatItem[] = chats.map((chat) => ({
    kind: 'chat',
    key: `chat:${chat.id}`,
    createdAt: toTimestamp(chat.created_at),
    chat,
  }));

  return [...visitItems, ...chatItems].sort((a, b) => {
    if (b.createdAt !== a.createdAt) return b.createdAt - a.createdAt;
    if (a.kind !== b.kind) return a.kind === 'visit' ? -1 : 1;
    return a.key.localeCompare(b.key);
  });
}
